import { FC, ReactNode } from "react";
import styled from "@emotion/styled";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextColor,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
} from "@/mds";
interface SettingsIntegrationHeaderProps {
  breadcrumb: string;
  title: string;
  isConnected?: boolean;
}

export const SettingsIntegrationHeader: FC<SettingsIntegrationHeaderProps> = ({
  breadcrumb,
  title,
  isConnected,
}) => {
  return (
    <StyledHeader>
      <MDSText
        variant={MDSTextVariant.TextXs}
        weight={MDSTextWeight.SemiBold}
        color={MDSTextColor.Secondary}
      >
        {breadcrumb}
      </MDSText>
      <StyledTitleRow>
        <MDSText variant={MDSTextVariant.HeaderL}>{title}</MDSText>
        {isConnected !== undefined && isConnected && (
          <StyledStatusBadge>
            <MDSText variant={MDSTextVariant.TextSm} weight={MDSTextWeight.SemiBold}>
              Connected
            </MDSText>
            <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
          </StyledStatusBadge>
        )}
      </StyledTitleRow>
    </StyledHeader>
  );
};

interface SettingsIntegrationBenefitCardProps {
  points: ReactNode[];
  variant?: "numbered" | "bullet";
}

export const SettingsIntegrationBenefitCard: FC<SettingsIntegrationBenefitCardProps> = ({
  points,
  variant = "numbered",
}) => {
  return (
    <StyledBenefitCard>
      {points.map((point, index) => (
        <StyledBenefitItem key={index}>
          <StyledBenefitMarker>
            <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
              {variant === "numbered" ? `${index + 1}.` : "\u2022"}
            </MDSText>
          </StyledBenefitMarker>
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Primary}>
            {point}
          </MDSText>
        </StyledBenefitItem>
      ))}
    </StyledBenefitCard>
  );
};

export const SettingsIntegrationPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  @container mds-panel (max-width: 750px) {
    overflow-y: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[7]} ${theme.spacing[3]}`};

  @container mds-panel (max-width: 750px) {
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]} ${theme.spacing[1]}`};
  }
`;

const StyledTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 700px;
`;

const StyledStatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

export const SettingsIntegrationScrollableArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
  padding: ${({ theme }) => theme.spacing[5]} 0;
  overflow-y: auto;
  flex: 1;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @container mds-panel (max-width: 750px) {
    overflow-y: visible;
    flex: none;
    padding-bottom: 40vh;
  }
`;

export const SettingsIntegrationSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${({ theme }) => theme.spacing[7]};

  @container mds-panel (max-width: 750px) {
    padding: 0 ${({ theme }) => theme.spacing[4]};
  }
`;

const StyledBenefitCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  background: ${({ theme }) => theme.colors.background.card.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing[5]};
  max-width: 700px;
`;

const StyledBenefitItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["0.5"]};
  align-items: flex-start;
`;

const StyledBenefitMarker = styled.div`
  width: 16px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
